import { API, graphqlOperation } from 'aws-amplify'
import { dbGetItem, listForecastedSales, metricDataByClientDate, metricDataByClientStoreDateMetric, storesByClientSorted } from 'graphql/queries'
import { createForecastedSales } from 'graphql/mutations'
import { addDays, addWeeks, format, isAfter, isEqual, parseISO, startOfWeek } from 'date-fns'
import { getPrevYearDate, getWeekStartDate, subDaysFromStrDate } from "utils/helperUtils";
import { fetchMetricDataByClientDate } from 'utils/metricDataUtils'


export const initialData = {
  week: '',
  store: '',
  avgWage: '',
  monProjSales: '',
  monForecastedSales: '',
  monOneYearAgoSales: '',
  monOneYearAgoNotes: '',
  monTwoYearsAgoSales: '',
  monTwoYearsAgoNotes: '',
  monProjAvgCheck: '',
  monSchedFOH: '',
  monSchedBOH: '',
  tueProjSales: '',
  tueForecastedSales: '',
  tueOneYearAgoSales: '',
  tueOneYearAgoNotes: '',
  tueTwoYearsAgoSales: '',
  tueTwoYearsAgoNotes: '',
  tueProjAvgCheck: '',
  tueSchedFOH: '',
  tueSchedBOH: '',
  wedProjSales: '',
  wedForecastedSales: '',
  wedOneYearAgoNotes: '',
  wedTwoYearsAgoSales: '',
  wedTwoYearsAgoNotes: '',
  wedProjAvgCheck: '',
  wedSchedFOH: '',
  wedSchedBOH: '',
  thuProjSales: '',
  thuForecastedSales: '',
  thuOneYearAgoNotes: '',
  thuTwoYearsAgoSales: '',
  thuTwoYearsAgoNotes: '',
  thuProjAvgCheck: '',
  thuSchedFOH: '',
  thuSchedBOH: '',
  friProjSales: '',
  friForecastedSales: '',
  friOneYearAgoNotes: '',
  friTwoYearsAgoSales: '',
  friTwoYearsAgoNotes: '',
  friProjAvgCheck: '',
  friSchedFOH: '',
  friSchedBOH: '',
  satProjSales: '',
  satForecastedSales: '',
  satOneYearAgoNotes: '',
  satTwoYearsAgoSales: '',
  satTwoYearsAgoNotes: '',
  satProjAvgCheck: '',
  satSchedFOH: '',
  satSchedBOH: '',
  sunProjSales: '',
  sunForecastedSales: '',
  sunOneYearAgoNotes: '',
  sunTwoYearsAgoSales: '',
  sunTwoYearsAgoNotes: '',
  sunProjAvgCheck: '',
  sunSchedFOH: '',
  sunSchedBOH: ''
}

export const initialTheoHours = {
  Mon: 0,
  Tue: 0,
  Wed: 0,
  Thu: 0,
  Fri: 0,
  Sat: 0,
  Sun: 0
}

 /* Function to set the week values to use in the
   * dropdown for the user to select the week for
   * the projections to be set
   */

export const setWeeks = () => {
  const today = new Date()
  const currentMonday = startOfWeek(today, { weekStartsOn: 1})
  const nextMonday = addWeeks(currentMonday, 1)
  const followingMonday = addWeeks(currentMonday, 2)

  const weeks = [
    format(currentMonday, 'yyyy-MM-dd'),
    format(nextMonday, 'yyyy-MM-dd'),
    format(followingMonday, 'yyyy-MM-dd')
  ]
  
  return weeks
}

export const weeks = setWeeks()


export async function getHistoricalSalesData(store, selectedWeek){
  
  const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
  let salesData = {}

  // Get sales data for most recently completed 2 weeks
  try {
    // Get current week
    const currentWeek = getWeekStartDate(new Date())
    const oneWeekAgo = subDaysFromStrDate(currentWeek, 7)
    const twoWeeksAgo = subDaysFromStrDate(currentWeek, 14)

    const pastWeeksSalesData = await fetchSalesData(oneWeekAgo, twoWeeksAgo, store)

    const twoWeeksAgoData = pastWeeksSalesData.week1
    const oneWeekAgoData = pastWeeksSalesData.week2

    days.forEach(day => {
      const average = ((oneWeekAgoData[day].actSales + twoWeeksAgoData[day].actSales)/2)
      salesData[`${day}Average`] = average.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    })

  } catch (err) {
    console.log('Error fetching pastWeeksSalesData', err)
    throw err
  }

  // Get sales data from 1 year ago and 2 years ago
  try {
    const oneYearAgo = getPrevYearDate(selectedWeek, 1)
    const twoYearsAgo = getPrevYearDate(selectedWeek, 2)

    const pastYearsSalesData = await fetchSalesData(oneYearAgo, twoYearsAgo, store)
    const twoYearsAgoData = pastYearsSalesData.week2
    const oneYearAgoData = pastYearsSalesData.week1

    days.forEach(day => {
      const roundedSales = Math.round(oneYearAgoData[day].actSales)
      salesData[`${day}OneYearAgoSales`] = roundedSales
      salesData[`${day}OneYearAgoDate`] = oneYearAgoData[day].date
      salesData[`${day}LastYearCheckCount`] = oneYearAgoData[day].checkCount
      salesData[`${day}OneYearAgoAvgCheck`] = oneYearAgoData[day].checkCount > 0
        ? (roundedSales / oneYearAgoData[day].checkCount).toFixed(2)
        : 0.00
      salesData[`${day}OneYearAgoNotes`] = oneYearAgoData[day].notes
      salesData[`${day}TwoYearsAgoSales`] = Math.round(twoYearsAgoData[day].actSales)
      salesData[`${day}TwoYearsAgoDate`] = twoYearsAgoData[day].date      
      salesData[`${day}TwoYearsAgoNotes`] = twoYearsAgoData[day].notes
    })
  } catch (err) {
    console.log('Error fetching pastYearsSalesData', err)
    throw err
  }

  return salesData
}

export async function getForecastedSales(store, selectedWeek){
  const startDate = selectedWeek
  const endDate = format(addDays(parseISO(startDate), 6), 'yyyy-MM-dd')
  
  const filter = {
    storeName: {
      eq: store.toLowerCase()
    },
    date: {
      between: [startDate, endDate]
    }
  }

  try {
    const forecastResponse = await API.graphql({
      query: listForecastedSales,
      variables: { filter }
    })

    console.log('forecastResponse', forecastResponse)

    const transformedForecastData = tranformForecastedSales(forecastResponse.data.listForecastedSales.items)
    return transformedForecastData
  } catch (error) {
    console.log('Error fetching forcasted sales', error)
  }
}

function tranformForecastedSales(forecastData){

  const result = {
    monForecastedSales: 0,
    tueForecastedSales: 0,
    wedForecastedSales: 0,
    thuForecastedSales: 0,
    friForecastedSales: 0,
    satForecastedSales: 0,
    sunForecastedSales: 0
  };

  forecastData.forEach(forecast => {
    const date = parseISO(forecast.date)
    const dayOfWeek = format(date, 'eee').toLowerCase()

    switch (dayOfWeek) {
      case 'mon':
        result.monForecastedSales = forecast.forecastedSales.toFixed(0);
        break;
      case 'tue':
        result.tueForecastedSales = forecast.forecastedSales.toFixed(0);
        break;
      case 'wed':
        result.wedForecastedSales = forecast.forecastedSales.toFixed(0);
        break;
      case 'thu':
        result.thuForecastedSales = forecast.forecastedSales.toFixed(0);
        break;
      case 'fri':
        result.friForecastedSales = forecast.forecastedSales.toFixed(0);
        break;
      case 'sat':
        result.satForecastedSales = forecast.forecastedSales.toFixed(0);
        break;
      case 'sun':
        result.sunForecastedSales = forecast.forecastedSales.toFixed(0);
        break;
      default:
        break;
    }
  })

  return result
}

async function fetchSalesData(week1, week2, store){

  const clientId = 'grm'
  const storeDataRaw = await getStoreMetadataByName(clientId, store)
  const parsedMetadata = JSON.parse(storeDataRaw.metadata)
  const toastGoLiveDate = parsedMetadata?.toast?.goLiveDate
  const storeId = storeDataRaw.storeId

  const useToastForWeek1 = isAfter(parseISO(week1), parseISO(toastGoLiveDate)) || isEqual(parseISO(week1), parseISO(toastGoLiveDate))
  const useToastForWeek2 = isAfter(parseISO(week2), parseISO(toastGoLiveDate)) || isEqual(parseISO(week2), parseISO(toastGoLiveDate))

  let alohaData = {}
  if (!useToastForWeek1 || !useToastForWeek2) {
    const adjustedWeek1 = useToastForWeek1 ? week2 : week1

    const dbVars = {
      report: 'r0003ActSalesAndNotes',
      week1: adjustedWeek1,
      week2,
      store
    }

    const dbVarsJSON = JSON.stringify(dbVars)

    try {
      const queryResponse = await API.graphql({
        query: dbGetItem,
        variables: {
          db: 'grm.reports',
          dbVariables: dbVarsJSON
        }
      })
      alohaData = JSON.parse(JSON.parse(queryResponse.data.dbGetItem.statusItem))
      console.log('alohaData', alohaData)
    } catch (err) {
      console.log('Error fetching Aloha sales data', err)
      throw err
    }
  }

  let toastData = {}

  async function fetchToastCheckCounts(startDateStr, targetStoreId, currentClientId) {

    const result = {}

    for (let i = 0; i < 7; i++) {
      const date = format(addDays(parseISO(startDateStr), i), 'yyyy-MM-dd')

      try {

        // const items = response?.data?.metricDataByClientDate?.items || []
        const items = await fetchMetricDataByClientDate({
          clientId: currentClientId,
          date: { eq: date },
          filter: {
            storeId: { eq: targetStoreId },
            or: [
              { metricName: { eq: 'checkCount' } },
              { metricName: { eq: 'netSales' } }
            ]
          }
        })

        let checkCount = 0
        let actSales = 0

        for (const item of items) {
          if (item.metricName === 'checkCount') {
            checkCount += item.numberValue || 0
          } else if (item.metricName === 'netSales') {
            actSales += item.numberValue || 0
          }
        }

        const dayKey = format(parseISO(date), 'eee').toLowerCase()

        result[dayKey] = {
          checkCount,
          actSales,
          notes: null,
          date
        }
      } catch (err) {
        console.log(`Error fetching Toast data for ${date}`, err)
      }
    }

    return result
  }  

  if (useToastForWeek1) {
    const toastWeek1 = await fetchToastCheckCounts(week1, storeId, clientId)
    Object.entries(toastWeek1).forEach(([day, val]) => {
      toastData[`${day}1`] = val
    })
  }

  if (useToastForWeek2) {
    const toastWeek2 = await fetchToastCheckCounts(week2, storeId, clientId)
    Object.entries(toastWeek2).forEach(([day, val]) => {
      toastData[`${day}2`] = val
    })
  }

  const week1Data = useToastForWeek1 ? normalizeToastWeek(toastData, '1') : addDatesToAlohaWeekData(alohaData.week1, week1);
  const week2Data = useToastForWeek2 ? normalizeToastWeek(toastData, '2') : addDatesToAlohaWeekData(alohaData.week2, week2);

  return {
    week1: week1Data,
    week2: week2Data
  }
}

function normalizeToastWeek(toastData, suffix) {
  const week = {}
  for (const day of ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']) {
    const data = toastData[`${day}${suffix}`] || {}
    week[day] = {
      actSales: data.actSales || 0,
      checkCount: data.checkCount || 0,
      notes: data.notes || null,
      date: data.date
    }
  }
  return week
}

function addDatesToAlohaWeekData(weekData, weekStartDateStr) {
  const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
  const startDate = parseISO(weekStartDateStr)
  const result = {}

  days.forEach((day, index) => {
    const base = weekData[day] || {}
    result[day] = {
      ...base,
      date: format(addDays(startDate, index), 'yyyy-MM-dd')
    }
  })

  return result
}

/**
 * Fetches metadata for a store by clientId and storeName.
 * @param {string} clientId - The client's ID.
 * @param {string} storeName - The name of the store (as stored in DB).
 * @returns {Promise<Object|null>} - The metadata object, or null if not found.
 */
export async function getStoreMetadataByName(clientId, storeName) {
  try {
    const response = await API.graphql({
      query: storesByClientSorted,
      variables: {
        clientId: clientId,
        storeName: { eq: storeName }
      },
      authMode: 'AMAZON_COGNITO_USER_POOLS'
    })
    console.log('response', response)

    const items = response?.data?.storesByClientSorted?.items
    if (!items || items.length === 0) {
      console.warn(`No store record found for storeName: ${storeName}`)
      return null
    }

    const storeId = items[0].id || null
    const metadata = items[0].metadata || null

    let returnObj
    if (storeId && metadata) {
      returnObj = { storeId, metadata }
    } else {
      returnObj = null
    }
    return returnObj
  } catch (err) {
    console.error('Error fetching store metadata:', err)
    throw err
  }
}

export function removeDayKeys(obj){
  const dayPrefixes = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
  const keyPatterns = [
    'Average',
    'OneYearAgoSales',
    'OneYearAgoNotes',
    'TwoYearsAgoSales',
    'TwoYearsAgoNotes',
    'ForecastedSales',
    'OneYearAgoDate',
    'LastYearCheckCount',
    'TwoYearsAgoDate',
    'OneYearAgoAvgCheck',
    'ProjCheckCount'
  ]

  const regex = new RegExp(`^(${dayPrefixes.join('|')})(${keyPatterns.join('|')})$`)

  Object.keys(obj).forEach(key => {
    if(regex.test(key)){
      delete obj[key]
    }
  })

  return obj
}

export async function writeToDynamoDB(record) {
  console.log('record', record)
  try {
    const result = await API.graphql({
      query: createForecastedSales,
      variables: {
        input: record
      }
    })
    console.log('writeToDynamoDB result', result)
    return result
  } catch (error) {
    console.log('Error inserting forecast record', error)
  } 
}

function cleanNumberInput(value) {
  if (typeof value === 'number'){
    return value
  }
  const num = Number(value)
  return isNaN(num) ? null : num
}

export function cleanProjectionData(formData){
  const cleanedData = {}
  const stringFields = ['week', 'store']

  for (const field in formData) {
    if (stringFields.includes(field)){
      cleanedData[field] = formData[field].trim()
    } else {
      cleanedData[field] = cleanNumberInput(formData[field])
    }
  }

  return cleanedData
}

export function calculateProjectedCheckCount(projectedSales, lyAvgCheck) {
  const sales = parseFormattedNumber(projectedSales)
  const avgCheck = parseFormattedNumber(lyAvgCheck)
  if (!sales || !avgCheck || avgCheck === 0) return 0
  return (sales / avgCheck).toFixed(2)
}

export function normalizeToTwoDecimals(value) {
  const num = parseFloat(value)
  return isNaN(num) ? '0.00' : num.toFixed(2)
}

export function parseFormattedNumber(value) {
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const cleaned = parseFloat(value.replace(/[$,\s]/g, ''))
    return isNaN(cleaned) ? 0 : cleaned
  }
  return 0
}

export function ensureProjectedMetricsAreDerived(data) {
  const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
  const dataCopy = { ...data }

  days.forEach(day => {
    const salesKey = `${day}ProjSales`
    const checkCountKey = `${day}ProjCheckCount`
    const avgCheckKey = `${day}ProjAvgCheck`
    const lyAvgCheckKey = `${day}OneYearAgoAvgCheck`

    const projSales = parseFormattedNumber(dataCopy[salesKey])
    const lyAvgCheck = parseFormattedNumber(dataCopy[lyAvgCheckKey])

    const projCheckCount = calculateProjectedCheckCount(projSales, lyAvgCheck)
    const projAvgCheck = projCheckCount > 0
      ? (projSales / projCheckCount).toFixed(2)
      : '0.00'

    dataCopy[checkCountKey] = projCheckCount
    dataCopy[avgCheckKey] = projAvgCheck
  })

  return dataCopy
}